import { inject, Injectable } from '@angular/core';
import { Observable, switchMap, takeWhile } from 'rxjs';
import { doc, docData, Firestore } from '@angular/fire/firestore';
import { select, Store } from '@ngrx/store';
import { selectCurrentUserAccountId } from '../../current-user/store/current-user.selectors';
import { filter } from 'rxjs/operators';
import { inputIsNotNullOrUndefined } from '../../data-access/input-is-not-null-or-undefined';
import { UserService } from '../../auth/services/user.service';
import { SlaRange } from '../../models/sla';
import { ScopeCaptions } from '../../models/scope-captions';

export type DistanceUnit = 'km' | 'mi';

export interface CompanySettings {
  theme?: string,
  logoURL?: string,
  secondLogoURL?: string,
  createdAt?: number,
  trackUsersLocation?: boolean,
  distanceUnit?: DistanceUnit,
  lastNetsuiteSyncAt?: number,
  categoriesLocked?: boolean,
  tagsLocked?: boolean,
  accountsDeleteLocked?: boolean,
  // undefined equals company
  paymentType?: 'individual' | 'company',
  // undefined equals events
  dashboardView?: 'events' | 'userReport',
  accountsLocked?: boolean,
  prospectsLocked?: boolean,
  childrenLocked?: boolean,
  massEmailHideToCc?: boolean,
  firstDayOfWeek?: number,
  dealsDisabled?: boolean,
  allowToUpdateCreatedAtForNotes?: boolean,
  allowGlobalAccountView?: boolean,
  slaRange?: SlaRange,
  allowAssignAccountToMe?: boolean,
  allowAccountAssignmentToAnyCompanyUser?: boolean,
  scopeCaptions?: ScopeCaptions,
}

@Injectable({
  providedIn: 'root',
})
export class GetCompanySettingsService {
  private readonly firestore = inject(Firestore);
  private readonly store$ = inject(Store);
  private readonly userService = inject(UserService);

  get(): Observable<CompanySettings | undefined> {
    return this.store$.pipe(
      select(selectCurrentUserAccountId),
      filter(inputIsNotNullOrUndefined),
      switchMap(accountId =>
        docData<CompanySettings>(doc(this.firestore, 'companySettings', accountId)).pipe(
          takeWhile(() => !!this.userService.currentUser),
        )
      ),
    );
  }
}
